import { addCollection, addIcon } from "@iconify/react";
import lucideIcons from "@iconify-json/lucide/icons.json";

/**
 * Registers every icon this app can render locally, so `<Icon icon="…">`
 * (anywhere in the app, today or in a future PR) never depends on a runtime
 * fetch to Iconify's public API.
 *
 * Without this, the very first time a given "prefix:name" is requested in a
 * session, @iconify/react fetches its SVG data over the network before it
 * can paint — icons already requested elsewhere (e.g. a sidebar nav icon,
 * mounted on every page) feel instant because they're already cached, while
 * one used only on a single page shows a blank flash on its first paint each
 * session. Registering the icon set locally removes that network dependency
 * entirely: import lucideIcons is the *entire* Lucide collection (~600KB,
 * one-time), so every "lucide:*" name — including ones not yet used anywhere
 * in the app — resolves synchronously from day one. Import this module once,
 * before anything renders (see main.tsx) — @iconify/react still falls back
 * to the network for any other icon set (e.g. this app's one "mdi:heart"),
 * so an occasional icon from a different set still works, just without the
 * instant-paint guarantee this gives every "lucide:*" icon.
 */
addCollection(lucideIcons);

// The app's one non-Lucide icon (DashboardView's heartbeat). Registered
// individually rather than pulling in the ~8MB @iconify-json/mdi package for
// a single icon — data copied from @iconify-json/mdi's icons.json.
addIcon("mdi:heart", {
  body: '<path fill="currentColor" d="m12 21.35l-1.45-1.32C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5c0 3.77-3.4 6.86-8.55 11.53z"/>',
  width: 24,
  height: 24,
});

// Password field icon on the focused auth screens (verification / reset
// password): a horizontal key inside a rounded square, drawn in Lucide's style
// (24px grid, 2px round strokes). Lucide has no key-in-a-square icon.
addIcon("msq:password-key", {
  body: '<g fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="5"/><circle cx="9" cy="12" r="2.5"/><path d="M11.5 12H17m-2 0v2"/></g>',
  width: 24,
  height: 24,
});
